import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';

import { AuthorizationSelectors } from '@next/core-lib/authorization';

import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ImportComponent } from '../../../shared/components/import/import.component';
import { PoultryContextKeys } from '../../../shared/constants';
import { SettingsServiceHelper } from '../../../shared/services/settingsHelperService';
import {
  AnimalType,
  FeedUnit,
  ReferenceValueSheet,
  ReferenceValueSheetRow,
  TemperatureUnit,
  WaterUnit,
  WeightUnit,
} from '../../models/reference-value-sheet.model';
import * as refValSheetActions from '../../store/reference-value-sheet.actions';
import { getReferenceValueSheets } from '../../store/reference-value-sheet.selectors';

@Component({
  selector: 'app-reference-value-sheet-overview',
  templateUrl: './reference-value-sheet-overview.component.html',
  imports: [
    PageToolbarComponent,
    MatButton,
    MatIcon,
    TableComponent,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ReferenceValueSheetOverviewComponent implements AfterViewInit, OnDestroy {
  /* SELECTORS & DATA */
  readonly refValSheets$ = this.store.select(getReferenceValueSheets);

  /* INDICATORS */
  isAdvisor = false;

  /* SUBSCRIPTIONS */
  subAuthorization: Subscription;
  subRefValSheets: Subscription;

  /* TEMPLATES */
  @ViewChild('columnNameTemplate') columnNameTemplate: TemplateRef<unknown>;
  @ViewChild('columnActiveTemplate') columnActiveTemplate: TemplateRef<unknown>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as ReferenceValueSheet[]),
    selectColumn: false,
    pagination: false,
    scrolling: true,
    width: '1200px',
    wrapperWidth: '100%',
    visibleCardColumns: 1,

    columns: [
      {
        id: 'name',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.name'),
      },
      {
        id: 'created',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.created'),
      },
      {
        id: 'updated',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.updated'),
      },
      {
        id: 'updatedAdvisorName',
        type: TableColumnTypes.STRING,
        label: this.translate('table-header.updatedAdvisorName'),
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.translate('buttons.edit'),
        callback: (refValSheet: ReferenceValueSheet) => {
          if (this.isAdvisor) {
            this.store.dispatch(
              refValSheetActions.editRefValSheetNavigation({
                refValSheet,
              }),
            );
          }
        },
        icon: 'edit',
        disabled: () => !this.isAdvisor,
      },
      {
        id: 'copyAsNew',
        callback: (refValSheet: ReferenceValueSheet) => this.copyAsNew(refValSheet),
        label: this.translate('buttons.copy-as-new'),
        icon: 'file_copy',
        disabled: () => false,
        primary: false,
      },
      {
        id: 'delete',
        label: this.translate('buttons.delete'),
        callback: (refValSheet: ReferenceValueSheet) => {
          if (this.isAdvisor) {
            this.store.dispatch(refValSheetActions.deleteRefValSheetConfirm({ refValSheet }));
          }
        },
        icon: 'delete',
        disabled: () => !this.isAdvisor,
      },
    ],
  };

  constructor(
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
    private readonly dialogService: DialogService,
    private readonly settingsService: SettingsServiceHelper,
  ) {
    // Set authorization roles indicators.
    this.subAuthorization = this.store
      .pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole))
      .subscribe((hasAtLeastAdvisorRole) => {
        this.isAdvisor = hasAtLeastAdvisorRole;
      });

    // Get feed-advices from state.
    this.subRefValSheets = this.refValSheets$.subscribe((refvalsheets) => {
      if (refvalsheets !== null && refvalsheets !== undefined && refvalsheets.length > 0) {
        this.tableConfig.dataSource.data = refvalsheets;
      } else {
        this.tableConfig.dataSource.data = [];
      }
    });
  }

  ngAfterViewInit() {
    // Set isActive column template.
    setTimeout(() => {
      let newConfig = { ...this.tableConfig };
      const columnName = newConfig.columns.find((c) => c.id === 'name');
      columnName.template = this.columnNameTemplate;

      newConfig = {
        ...newConfig,
        columns: [
          ...this.tableConfig.columns,
          {
            id: 'isActive',
            type: TableColumnTypes.TEMPLATE,
            label: this.translate('table-header.isActive'),
          },
        ],
      };
      const columnActive = newConfig.columns.find((c) => c.id === 'isActive');
      columnActive.template = this.columnActiveTemplate;

      this.tableConfig = newConfig;
    }, 50);
  }

  translate(key: string): string {
    return this.localeService.translate(`reference-value-sheet.overview.${key}`);
  }

  uploadCSV() {
    this.dialogService
      .dialog(ImportComponent, {
        title: this.localeService.translate('reference-value-sheet.overview.upload-csv-title'),
        cancelButtonText: this.localeService.translate('cancel'),
        confirmButtonText: this.localeService.translate('save'),
      })
      .pipe(take(1))
      .subscribe((result) => {
        const parseValue = (value: string): number => parseFloat(value.replace(',', '.'));
        if (result.status === DIALOG_RETURN_TYPES.confirm && result.result?.file) {
          this.store
            .select(UserSelectors.selectUser)
            .pipe(take(1))
            .subscribe((user: AgrUser) => {
              const reader: FileReader = new FileReader();
              reader.onload = (e) => {
                const referenceValueSheetRows: ReferenceValueSheetRow[] = [];
                const lines: string[] = (e.target.result as string).split('\r\n');
                lines.forEach((line, index) => {
                  if (line.length > 0) {
                    const lineValues = line.split(';');
                    if (!isNaN(Number(lineValues[0]))) {
                      referenceValueSheetRows.push(<ReferenceValueSheetRow>{
                        id: index,
                        day: parseValue(lineValues[0]),
                        temperatureInside: parseValue(lineValues[1]),
                        relativeHumidity: parseValue(lineValues[2]),
                        feedIntakePerChickPerDay: parseValue(lineValues[3]),
                        waterIntake: parseValue(lineValues[4]),
                        weight: parseValue(lineValues[5]),
                        mortality: parseValue(lineValues[6]),
                        numberOfHoursLight: parseValue(lineValues[7]),
                        lightIntensityInLux: parseValue(lineValues[8]),
                      });
                    }
                  }
                });
                this.settingsService
                  .getSingle(
                    null,
                    PoultryContextKeys.REFERENCE_VALUE_SHEET,
                    SettingsStorage.AppLocal,
                  )
                  .subscribe((base: ReferenceValueSheet) => {
                    const fileName = result.result.file.name;
                    const firstDotIndex = fileName.indexOf('.');
                    const name =
                      firstDotIndex >= 0 && firstDotIndex < fileName.length - 1
                        ? fileName.slice(0, firstDotIndex)
                        : fileName;
                    const refValSheet = <ReferenceValueSheet>{
                      ...base,
                      name,
                      description: `imported file: ${result.result.file.name}`,
                      updatedAdvisorName: user.name,
                      createdAdvisorName: user.name,
                      created: new Date(result.result.file.lastModified).toISOString(),
                      updated: new Date(result.result.file.lastModified).toISOString(),
                      isStandardReferenceValueSheet: true,
                      country: 'NL',
                      isActive: true,
                      temperatureUnit: TemperatureUnit.Celcius,
                      feedUnit: FeedUnit.Gram,
                      waterUnit: WaterUnit.ML,
                      weightUnit: WeightUnit.Gram,
                      animalType: AnimalType.REARING,
                      referenceValueSheetRows,
                    };
                    this.store.dispatch(
                      refValSheetActions.upsertRefValSheetRequest({ refValSheet }),
                    );
                  });
              };
              reader.readAsText(result.result?.file);
            });
        }
      });
  }

  downloadTemplate() {
    const link = document.createElement('a');
    link.setAttribute('type', 'hidden');
    link.href = '/assets/data/poultrynext-rearing_referencevaluesheet-template.csv';
    link.download = 'poultrynext-rearing_referencevaluesheet-template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  createRefValSheet() {
    if (this.isAdvisor) {
      this.store.dispatch(refValSheetActions.createRefValSheetNavigation());
    }
  }

  copyAsNew(refValSheet: ReferenceValueSheet) {
    this.store.dispatch(refValSheetActions.copyRefValSheetAsNewRequest({ refValSheet }));
  }

  onToggleActive(refValSheet: ReferenceValueSheet) {
    if (this.isAdvisor) {
      const lRefValSheet = { ...refValSheet };
      lRefValSheet.isActive = !refValSheet.isActive;
      this.store.dispatch(
        refValSheetActions.upsertRefValSheetRequest({ refValSheet: lRefValSheet }),
      );
    }
  }

  onViewRefValSheet(refValSheet: ReferenceValueSheet) {
    this.store.dispatch(
      refValSheetActions.viewRefValSheetNavigation({
        refValSheet: { ...refValSheet },
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.subAuthorization) {
      this.subAuthorization.unsubscribe();
    }

    if (this.subRefValSheets) {
      this.subRefValSheets.unsubscribe();
    }
  }
}
